import { makeStyles } from '@material-ui/styles'

import { useAppSelector } from 'app/store'
import PollingData from 'core/components/PollingData'
import { ViewMode } from 'core/components/ViewModeToggle'
import Progress from 'core/components/progress/Progress'
import EmptyContent from 'core/components/statusCard/EmptyContent'
import useListAction from 'core/hooks/useListAction'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import React, { useCallback, useEffect, useState } from 'react'
import RadioDropdownFilter from '../../../../networks/dns/records/RadioDropdownFilter'
import { IAppsDetailsPageTabs } from '../../../model'
import { listApplications, listTemplates } from '../../../repositories/actions'
import { allAppSelector, templateSelector } from '../../../repositories/selector'
import AppHeaderPrimaryActions from '../../AppHeaderPrimaryActions'
import CommonActionPopover from '../../CommonActionPopover'
import VmsDetailsModal from '../../VmsDetailsModal'
import { allAppActions, filterAppsByStateAndVersion, getStateList } from '../../helper'
import AppCardView from '../AppCardView'
import AppListView from '../AppListView'
import CreateNewAppModal from '../CreateNewAppModal'
import DeleteApp from '../DeleteApp'
import { gridContainer } from '../../commonStyles'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import { disabledCreateAppAdminMessage, disabledCreateAppSSUUserMessage } from '../../constant'

const useStyles = makeStyles<Theme>((theme) => ({
  grid: {
    display: 'grid',
    paddingBottom: theme.spacing(1),
  },
  gridContainer: gridContainer(theme),
  gridItem: {
    width: '100%',
  },
  actions: {
    display: 'grid',
    gridAutoColumns: 'auto max-content',
    gridAutoFlow: 'column',
    gap: 16,
  },
  filtersContainer: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  appInfoFooterTitle: {
    color: theme.components.typography.passive,
    textTransform: 'uppercase',
  },
  divider: {
    margin: theme.spacing(1, 0, 2, 0),
  },
  sharedContainer: {
    marginTop: theme.spacing(3),
  },
  progressOverlay: {
    maxHeight: '50%',
    boxSizing: 'border-box',
  },
  emptyContent: {
    paddingTop: '10%',
  },
}))

const AllApps = () => {
  const [view, setView] = useState<ViewMode>(ViewMode.LIST)
  const classes = useStyles()
  const oneSecond = 1000
  const { loading, reload } = useListAction(listApplications)
  const { loading: loadingTemplates, reload: reloadTemplates } = useListAction(listTemplates)
  const templateDetails = useAppSelector(templateSelector)
  const appDetails = useAppSelector(allAppSelector)
  const [filteredData, setFilteredData] = useState(appDetails)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showVmsModal, setShowVmsModal] = useState(false)
  const [rowData, setRowData] = useState(null)
  const [selectedState, setSelectedState] = useState([])
  const [open, setOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const filteredApps = filterAppsByStateAndVersion(filteredData, selectedState)
  const [selectedApp, setSelectedApp] = useState(null)

  const handlePopoverClick = useCallback((event, item) => {
    setAnchorEl(event.currentTarget)
    setSelectedApp(item) // set the selected card's data
    setOpen(true)
  }, [])
  const handleSearch = useCallback(
    (filteredResults) => {
      setFilteredData(filteredResults?.appDetails)
    },
    [appDetails],
  )
  const handleApplyFilter = ({ selectedItems }) => {
    setSelectedState(Array.isArray(selectedItems) ? selectedItems : [selectedItems])
  }
  const handleReload = useCallback(async (refetch = false, updateLoadingState = true) => {
    reload(refetch, updateLoadingState)
    reloadTemplates(refetch, updateLoadingState)
  }, [])
  useEffect(() => {
    setFilteredData(appDetails)
  }, [appDetails])

  return (
    <>
      <PollingData
        hidden
        loading={loading || loadingTemplates}
        onReload={handleReload}
        refreshDuration={oneSecond * 30}
      />
      <CreateNewAppModal
        addRoute={routeManager.routes.openstack.createApps}
        tabName={IAppsDetailsPageTabs.Allapps}
      />
      {showDeleteDialog && selectedApp && (
        <DeleteApp
          rows={[selectedApp]}
          onClose={() => {
            setShowDeleteDialog(false)
            setSelectedApp(null)
          }}
        />
      )}
      {showVmsModal && (
        <VmsDetailsModal
          data={rowData}
          showVmsModal={showVmsModal}
          setShowVmsModal={setShowVmsModal}
        />
      )}
      {/* <VmSlider value={vmCount} onChange={setVmCount} /> */}
      <AppHeaderPrimaryActions
        addUrl={routeManager.routes.openstack.createApps.path({
          tab: IAppsDetailsPageTabs.Allapps,
        })}
        addText="Create New App"
        view={view}
        setView={setView}
        handleSearch={handleSearch}
        data={{ appDetails }}
        searchKeys={['headerName', 'templateName', 'ownerName']}
        onReload={handleReload}
        disableAddButton={!templateDetails?.length}
        disabledAddButtonInfo={
          isAdmin() ? disabledCreateAppAdminMessage : disabledCreateAppSSUUserMessage
        }
        HeaderActions={
          <div className={classes.filtersContainer}>
            <RadioDropdownFilter
              label="State"
              items={getStateList(appDetails)}
              loading={false}
              handleApplyFilter={(selectedItems) => handleApplyFilter({ selectedItems })}
            />
            {/* <RadioDropdownFilter
              label="Version"
              items={getVersionList(appDetails)}
              onChange={(selectedItems) =>
                setSelectedVersion(Array.isArray(selectedItems) ? selectedItems : [selectedItems])
              }
              handleApplyFilter={handleApplyFilter}
            /> */}
          </div>
        }
      >
        <Progress
          loading={loading}
          message={'Loading All Apps...'}
          overlay
          className={classes.progressOverlay}
        >
          {filteredApps && filteredApps?.length > 0 ? (
            view === ViewMode.LIST ? (
              <>
                {filteredApps?.map((item) => (
                  <div className={classes.grid} key={item.uid}>
                    <AppListView
                      iconName={item?.iconName}
                      headerName={item?.headerName}
                      status={item?.status}
                      vms={item?.vms}
                      version={item?.version}
                      templateName={item?.templateName}
                      ownerName={item?.ownerName}
                      overviewActions={allAppActions(item, setShowVmsModal, setRowData)}
                      handleClick={(e) => handlePopoverClick(e, item)}
                      isIconView={false}
                      data={item}
                    />
                  </div>
                ))}
              </>
            ) : (
              <>
                <div className={classes.gridContainer}>
                  {filteredApps?.map((item) => (
                    <div key={item?.uid} className={classes.gridItem}>
                      <AppCardView
                        iconName={item?.iconName}
                        headerName={item?.headerName}
                        status={item?.status}
                        vms={item?.vms}
                        version={item?.version}
                        templateName={item?.templateName}
                        ownerName={item?.ownerName}
                        overviewActions={allAppActions(item, setShowVmsModal, setRowData)}
                        handleClick={(e) => handlePopoverClick(e, item)}
                        isIconView={false}
                        data={item}
                      />
                    </div>
                  ))}
                </div>
              </>
            )
          ) : (
            <div className={classes.emptyContent}>
              <EmptyContent
                title={'No Apps Found'}
                subTitle={'Create one in order to see details'}
              />
            </div>
          )}
        </Progress>
      </AppHeaderPrimaryActions>
      <CommonActionPopover
        open={open}
        setOpen={setOpen}
        anchorEl={anchorEl}
        actions={[
          //   {
          //     label: 'Edit',
          //     icon: 'edit',
          //     onClick: () => {
          //       history.push(
          //         routeManager.routes.openstack.createApps.path({
          //           tab: IAppsDetailsPageTabs.Myapps,
          //         }),
          //       )
          //     },
          //     disabled: false,
          //     tooltipMessage: 'NS and SOA type records cannot be edited.',
          //   },
          {
            label: 'Delete',
            icon: 'trash-can',
            // onClick: () => setShowDeleteDialog(true),
            onClick: () => {
              setShowDeleteDialog(true)
              setOpen(false)
            },
            disabled: selectedApp?.status === 'deleting' ? true : false,
          },
        ]}
      />
    </>
  )
}

export default AllApps
